import { Injectable } from '@angular/core';

export interface League {
  id?: number;
  name: string;
  max_teams: number;
  team_budget: number;
  roster_size: number;
  draft_datetime?: string | null;
  team_name?: string;
  team_count?: number;
  hash?: string;
  created_at?: string;
  updated_at?: string;
  role?: 'owner' | 'commissioner' | 'member' | 'viewer';
  is_commissioner?: boolean;
}

export interface LeaguePreview {
  name: string;
  max_teams: number;
  member_count: number;
  spots_available: number;
  is_member: boolean;
  is_full: boolean;
}

export interface RosterMember {
  player_id: number;
  first_name: string;
  last_name: string;
  position: string;
  pro_team: string;
  cost: number;
  draft_position: number;
}

export interface TeamRoster {
  id: number;
  name: string;
  user_name: string | null;
  user_profile_picture: string | null;
  roster: RosterMember[];
  total_spent: number;
  remaining_budget: number;
}

export interface LeagueRosters {
  league_name: string;
  team_budget: number;
  roster_size: number;
  teams: TeamRoster[];
}

export interface TeamStats {
  id: number;
  name: string;
  user_name: string | null;
  total_spent: number;
  remaining_budget: number;
  roster_count: number;
  position_counts: Record<string, number>;
  position_spending: Record<string, number>;
}

export interface DraftHistoryEntry {
  draft_position: number;
  cost: number;
  player_id: number;
  first_name: string;
  last_name: string;
  position: string;
  pro_team: string;
  photo: string | null;
  team_id: number;
  team_name: string;
  user_id: number;
  user_name: string;
}

export interface LeagueStats {
  league_name: string;
  team_budget: number;
  roster_size: number;
  max_teams: number;
  teams: TeamStats[];
  draft_history: DraftHistoryEntry[];
}

export interface JoinResult {
  message: string;
  league: League;
}

@Injectable({
  providedIn: 'root',
})
export class LeagueService {
  private readonly apiUrl = '/api/leagues';

  async getAllLeagues(): Promise<League[]> {
    try {
      const response = await fetch(this.apiUrl, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to fetch leagues');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching leagues:', error);
      throw error;
    }
  }

  async getLeague(id: number): Promise<League> {
    try {
      const response = await fetch(`${this.apiUrl}/${id}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to fetch league');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching league:', error);
      throw error;
    }
  }

  async getLeagueByHash(hash: string): Promise<League> {
    try {
      const response = await fetch(`${this.apiUrl}/draft/${hash}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to fetch league');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching league by hash:', error);
      throw error;
    }
  }

  private toUtcIso(league: League): League {
    if (!league.draft_datetime) return league;
    return { ...league, draft_datetime: new Date(league.draft_datetime).toISOString() };
  }

  async createLeague(league: League): Promise<League> {
    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(this.toUtcIso(league)),
      });
      if (!response.ok) {
        throw new Error('Failed to create league');
      }
      return await response.json();
    } catch (error) {
      console.error('Error creating league:', error);
      throw error;
    }
  }

  async updateLeague(id: number, league: League): Promise<League> {
    try {
      const response = await fetch(`${this.apiUrl}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(this.toUtcIso(league)),
      });
      if (!response.ok) {
        throw new Error('Failed to update league');
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating league:', error);
      throw error;
    }
  }

  async updateTeamName(leagueId: number, teamName: string): Promise<League> {
    try {
      const response = await fetch(`${this.apiUrl}/${leagueId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ team_name: teamName }),
      });
      if (!response.ok) {
        throw new Error('Failed to update team name');
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating team name:', error);
      throw error;
    }
  }

  async deleteLeague(id: number): Promise<void> {
    try {
      const response = await fetch(`${this.apiUrl}/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to delete league');
      }
    } catch (error) {
      console.error('Error deleting league:', error);
      throw error;
    }
  }

  async getLeaguePreview(hash: string): Promise<LeaguePreview> {
    const response = await fetch(`${this.apiUrl}/preview/${hash}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('League not found');
      }
      throw new Error('Failed to fetch league preview');
    }
    return await response.json();
  }

  async getLeagueRosters(id: number): Promise<LeagueRosters> {
    try {
      const response = await fetch(`${this.apiUrl}/${id}/rosters`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to fetch league rosters');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching league rosters:', error);
      throw error;
    }
  }

  async getLeagueStats(id: number): Promise<LeagueStats> {
    const response = await fetch(`${this.apiUrl}/${id}/stats`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Failed to fetch league stats');
    }
    return await response.json();
  }

  async joinLeague(hash: string): Promise<JoinResult> {
    const response = await fetch(`/api/join/${hash}`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to join league');
    }
    return await response.json();
  }
}
